import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelope, faPhone } from "@fortawesome/pro-solid-svg-icons"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchKitchenDetails, UserWeekMenu } from "../../weekMenuApi"
import { KitchenDetails, KitchenDetailsOpeningTime } from "../../weekMenuTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectKitchen } from "../../../../nonAdminUserSelectors"
import { useExternalLinks } from "../../../../../../shared/hooks/useAppLinks"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { DateTimeFactory, useDateTime } from "../../../../../localization/useDateTime"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"

const useStyles = makeStyles()((theme) => ({
    container: {
        color: "black",
        backgroundColor: tailwindPalette.blue[200],
        borderRadius: ".75rem",
        padding: "1rem 1.25rem",
        [theme.breakpoints.up("xl")]: {
            padding: "1.25rem 2.25rem",
        },
    },
    section: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
    },
    timeSection: {
        borderRadius: "0.625rem",
        padding: "1rem",
        backgroundColor: "#FFFFFF40",
        color: tailwindPalette.blue[900],
    },
    sectionIconContainer: {
        display: "flex",
        alignItems: "center",
        width: "1.5rem",
        height: "1.5rem",
        justifyContent: "center",
    },
    schedule: {
        fontSize: "3.65rem",
        fontWeight: 500,
        textAlign: "center",
    },
    skeletonCard: {
        height: "9.2rem",
        borderRadius: "0.625rem",
        width: "100%",
    },
    skeletonLine: {
        height: "1.3rem",
        borderRadius: "0.625rem",
        width: "80%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
}))

type SafeCanteenInfoProps = Readonly<{
    kitchenId?: number
}>

export function SafeCanteenInfo({ kitchenId }: SafeCanteenInfoProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const { data, isLoading } = useApiQuery(fetchKitchenDetails, {
        queryName: UserWeekMenu.KITCHEN_DETAILS,
        dependencies: {
            kitchenId: kitchenId ?? 0,
        },
        enabled: Boolean(kitchenId),
        useErrorBoundary: true,
        select: transformKitchenDetails,
        refetchInterval: RefetchIntervals.medium,
    })

    const handleExternalLink = useExternalLinks()
    const groupedHours = groupOpeningHours(dateTimeFactory, data?.contactDetails.openingTimes)

    return (
        <>
            <Box className={cx(classes.section)}>
                <LocalizedStrict id="user-home-canteen-contact-canteen">
                    <Typography fontWeight={500} variant="h5">
                        Contact canteen
                    </Typography>
                </LocalizedStrict>

                {isLoading && <Skeleton variant="rectangular" className={classes.skeletonLine} />}

                <ErrorBoundary
                    fallback={
                        <UserDashboardErrorFallbackCard
                            classNames={classes.errorCard}
                            contentClassNames={classes.errorContent}
                        />
                    }
                >
                    <>
                        {data?.contactDetails.phoneNumber && (
                            <Stack direction="row" alignItems="center" spacing="0.5rem">
                                <Box className={classes.sectionIconContainer}>
                                    <FontAwesomeIcon icon={faPhone} color="rgb(130, 120, 142)" />
                                </Box>
                                <Typography variant="body1" color="#000000DE">
                                    {data?.contactDetails.phoneNumber}
                                </Typography>
                            </Stack>
                        )}
                        {data?.contactDetails.email && (
                            <Stack
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleExternalLink(`mailto:${data?.contactDetails.email}`)}
                                direction="row"
                                alignItems="center"
                                spacing="0.5rem"
                            >
                                <Box className={classes.sectionIconContainer}>
                                    <FontAwesomeIcon icon={faEnvelope} color="rgb(130, 120, 142)" />
                                </Box>
                                <Typography variant="body1" color="#000000DE">
                                    {data?.contactDetails.email}
                                </Typography>
                            </Stack>
                        )}
                    </>
                </ErrorBoundary>
            </Box>

            {isLoading ? (
                <Box className={classes.section}>
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                </Box>
            ) : (
                <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                    <Box className={cx(classes.section, classes.timeSection)}>
                        <Stack spacing="0.5rem">
                            {groupedHours.map((group, index) => (
                                <Box key={index}>
                                    <Typography className={classes.schedule}>{group.openingTime}</Typography>
                                    <Typography variant="subtitle1" color="#00000099" textAlign="center">
                                        {group.days.length > 1
                                            ? `${group.days[0]}-${group.days[group.days.length - 1]}`
                                            : group.days[0]}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </ErrorBoundary>
            )}
        </>
    )
}

export function CanteenInfo() {
    const { classes } = useStyles()
    const selectedKitchen = useAppSelector(selectKitchen)

    if (!selectedKitchen) {
        return null
    }

    return (
        <Stack direction={{ xs: "column", md: "row" }} className={classes.container} marginBottom="3rem" spacing="2rem">
            <QueryErrorBoundary Fallback={CanteenFallback}>
                <SafeCanteenInfo kitchenId={selectedKitchen?.id} />
            </QueryErrorBoundary>
        </Stack>
    )
}

const CanteenFallback = () => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box marginBottom="3rem">
            <UserDashboardErrorFallbackCard
                classNames={classes.errorCard}
                contentClassNames={classes.errorContent}
                errorMessage={l10n.getString("user-dashboard-error-merchant-contact-unavailable")}
            >
                <LocalizedStrict id="user-home-canteen-contact-canteen">
                    <Typography fontWeight={500} variant="h5">
                        Contact canteen
                    </Typography>
                </LocalizedStrict>
            </UserDashboardErrorFallbackCard>
        </Box>
    )
}

const transformKitchenDetails = (data: KitchenDetails) => data

const groupOpeningHours = (dateTimeFactory: DateTimeFactory, data?: KitchenDetailsOpeningTime[]) => {
    if (!data || data.length === 0) return []

    const sortedData = [...data].sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    const grouped: { days: string[]; openingTime: string }[] = []
    let currentGroup: { days: string[]; openingTime: string } | null = null

    sortedData.forEach(({ displayName, openingTime, closingTime }) => {
        const openHour = dateTimeFactory.fromHourMinute(openingTime).hour
        const closeHour = dateTimeFactory.fromHourMinute(closingTime).hour
        const formattedTime = `${openHour}-${closeHour}`

        if (!currentGroup || currentGroup.openingTime !== formattedTime) {
            currentGroup = { days: [displayName], openingTime: formattedTime }
            grouped.push(currentGroup)
        } else {
            currentGroup.days.push(displayName)
        }
    })

    return grouped
}
